// This component should not make any call to backend but gets its dynamic data as an input prop.
import React, {
  useState,
  useCallback,
  useContext,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { StateContext } from '../../../../GlobalStateStore';
import Sheet from '@amzn/meridian/sheet';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Heading from '@amzn/meridian/heading';
import Divider from '@amzn/meridian/divider';
import Box from '@amzn/meridian/box';
import { CSAItem } from '../../../../Types/Metric';
import { urls } from '../../../../Util';
import { FormattedMessage } from 'react-intl';
import DateCompare from '../../../../Types/DateCompare';
import { useNavigate } from 'react-router';

export interface CSAListSheetProps {
  csas: CSAItem[];
  label: string;
  clickstream: {
    sectionType: string;
    linkType: string;
    dateSelection: string;
  };
  onSheetVisibilityChange?: (open: boolean) => void;
}

export interface CSASheetRef {
  showSheet: () => void;
  hideSheet: () => void;
}

const CSAListSheet = forwardRef<CSASheetRef, CSAListSheetProps>(
  function CSAList(
    props: CSAListSheetProps,
    ref: React.Ref<CSASheetRef>
  ): React.ReactElement {
    const globalState = useContext(StateContext);
    const { inspireOptions } = globalState.state;
    const { isTm, requesterUserID, requesterUsername } = inspireOptions.data;
    const { clickstream } = props;

    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const onClickOpen = useCallback(() => setOpen(true), []);
    const onClickClose = useCallback(() => {
      setOpen(false);
      props.onSheetVisibilityChange && props.onSheetVisibilityChange(false);
    }, []);
    const onClose = useCallback(() => setOpen(false), []);

    useImperativeHandle(ref, () => ({
      showSheet: () => {
        setOpen(true);
      },
      hideSheet: () => {
        setOpen(false);
      },
    }));

    const commonClickstreamData = {
      'data-csa-c-type': 'button',
      'data-csa-c-slot-id': 'metrics-coaching-details-sheet-slot',
      'data-csa-c-tag1': 'Metrics',
      'data-csa-c-tag2': 'Coaching Details',
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-is-tm': isTm,
    };

    const enDefaultMessageCoachingDeliveredCSA =
      clickstream.dateSelection === DateCompare.DAY
        ? 'today'
        : clickstream.dateSelection === DateCompare.WEEK
          ? 'this week'
          : 'this month';

    return (
      <>
        <Column alignmentVertical='top' heights='fill' data-cy='side-sheet'>
          <Sheet type='push' side='end' open={open} onClose={onClose}>
            <Row width='100%' widths={['232px', '24px']}>
              <Text type='h300'>
                {props.label} ({props.csas.length})
              </Text>
              <Button onClick={onClickClose} type='icon'>
                <Icon tokens={closeTokens}>Close sheet</Icon>
              </Button>
            </Row>
            {props.csas.length > 0 ? (
              props.csas.map(csa => (
                <Box
                  type='outline'
                  spacingInset='400'
                  data-cy='side-sheet-content'
                >
                  <Heading level={1} type='h100'>
                    {csa.fullName}
                  </Heading>
                  {csa.coachingCount !== undefined && (
                    <Text>
                      <FormattedMessage
                        id={`cs_perfportal_metrics_coachings_delivered_csa_${clickstream.dateSelection}`}
                        defaultMessage={`${csa.coachingCount} coachings delivered ${enDefaultMessageCoachingDeliveredCSA}`}
                        values={{
                          numOfCoachings: csa.coachingCount,
                        }}
                      />
                    </Text>
                  )}
                  <Divider spacingBefore='200' spacingAfter='200' />
                  <Button
                    minWidth='100%'
                    size='small'
                    href={`${urls.coachingNotes}/${csa.userId}`}
                    onClick={navigate}
                    type='tertiary'
                    {...commonClickstreamData}
                    data-csa-c-content-id={`metrics-coaching-details-sheet-${clickstream.dateSelection}-${clickstream.sectionType}-${clickstream.linkType}-${csa.userId}`}
                    data-csa-c-tag3={clickstream.sectionType}
                    data-csa-c-tag4={clickstream.linkType}
                  >
                    <FormattedMessage
                      id='cs_perfportal_metrics_go_to_coaching'
                      defaultMessage='Go to Coaching Page'
                    />
                  </Button>
                </Box>
              ))
            ) : (
              <p>
                <FormattedMessage
                  id='cs_perfportal_nothing_to_show'
                  defaultMessage='Nothing to show yet.'
                />
              </p>
            )}
          </Sheet>
        </Column>
      </>
    );
  }
);

export default CSAListSheet;
